import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { BookOpen, MessageCircle, Sprout, Users } from "lucide-react";
import { PhoneFrame } from "@/components/storyboard/PhoneStoryboard";
import type { StoryboardScreenId } from "@/components/storyboard/storyboardData";
// Bundled locally (AI-11994) — external Unsplash URLs failed to load in CI.
import companionWord from "@/assets/landing/1509021436665-8f07dbf5bf1d.jpg";
import companionAsk from "@/assets/landing/1519682337058-a94d519337bc.jpg";
import companionRhythm from "@/assets/landing/1471193945509-9ad0617afabf.jpg";
import companionCircles from "@/assets/landing/1529156069898-49953e39b3ac.jpg";

type CompanionPreview = {
  id: StoryboardScreenId;
  label: string;
  title: string;
  detail: string;
  image: string;
  imageAlt: string;
  icon: ReactNode;
};

const PREVIEWS: CompanionPreview[] = [
  {
    id: "verse",
    label: "Daily Word",
    title: "Scripture first, every morning.",
    detail: "A verse, a theme, an application, and a prayer prompt reveal in sequence instead of landing as a flat quote card.",
    image: companionWord,
    imageAlt: "Open Bible on a table in warm morning light",
    icon: <BookOpen size={16} />,
  },
  {
    id: "chat",
    label: "Ask",
    title: "Talk through the verse.",
    detail: "The companion explains why a verse was chosen, keeps scripture visible, and gives a next step without pretending to replace pastoral care.",
    image: companionAsk,
    imageAlt: "Person reading a book in soft window light",
    icon: <MessageCircle size={16} />,
  },
  {
    id: "vine",
    label: "Rhythm",
    title: "A Bible-app rhythm with grace.",
    detail: "Faith Vine replaces a bare streak counter with visible growth, a grace token, and a simple return path for tomorrow.",
    image: companionRhythm,
    imageAlt: "New green growth in a garden",
    icon: <Sprout size={16} />,
  },
  {
    id: "groups",
    label: "Circles",
    title: "Prayer with your people.",
    detail: "Prayer circles show shared momentum at the group level: encouraging, private, and never a blame ledger.",
    image: companionCircles,
    imageAlt: "Friends sitting together outdoors",
    icon: <Users size={16} />,
  },
];

export function CompanionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [titleOpacity, setTitleOpacity] = useState(1);
  const [activeId, setActiveId] = useState<StoryboardScreenId>("verse");
  const rafRef = useRef<number | null>(null);
  const active = PREVIEWS.find((preview) => preview.id === activeId) ?? PREVIEWS[0];

  const updateTitleOpacity = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const sectionHeight = sectionRef.current.offsetHeight;
    const scrollableRange = Math.max(1, sectionHeight - windowHeight);
    const scrolled = -rect.top;
    const progress = Math.max(0, Math.min(1, scrolled / scrollableRange));
    setTitleOpacity(Math.max(0.14, 1 - progress * 1.35));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateTitleOpacity);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    updateTitleOpacity();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateTitleOpacity]);

  return (
    <section className="bg-[#FAFAF7]">
      <div ref={sectionRef} className="relative" style={{ height: "175vh" }}>
        <div className="sticky top-0 flex min-h-screen items-center justify-center overflow-hidden px-6 py-24 md:px-12 lg:px-20">
          <div className="relative w-full max-w-7xl">
            <div
              className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center"
              style={{ opacity: titleOpacity }}
            >
              <h2 className="px-6 text-center text-[14vw] font-medium leading-[0.9] tracking-tighter text-neutral-900/10 md:text-[10vw] lg:text-[8vw]">
                Meet your companion.
              </h2>
            </div>

            <div className="relative z-10 grid items-center gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12">
              <div className="relative z-20 order-2 lg:order-1">
                <div className="overflow-hidden rounded-[2rem] border border-[#e5dac8] bg-white shadow-[0_34px_90px_-58px_rgba(31,28,20,0.65)]">
                  <div className="relative h-[330px] overflow-hidden sm:h-[420px]">
                    <img
                      key={active.image}
                      src={active.image}
                      alt={active.imageAlt}
                      className="absolute inset-0 h-full w-full object-cover transition duration-700 ease-out"
                      loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111015]/80 via-[#111015]/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white sm:p-7">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#f4d18d]">
                        Bible app-style rhythm
                      </p>
                      <h3 className="max-w-sm text-3xl font-semibold leading-tight">{active.title}</h3>
                      <p className="mt-3 max-w-md text-sm leading-6 text-white/78">{active.detail}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4">
                    {PREVIEWS.map((preview) => {
                      const isActive = preview.id === activeId;
                      return (
                        <button
                          key={preview.id}
                          type="button"
                          aria-pressed={isActive}
                          data-testid={`companion-preview-${preview.id}`}
                          onClick={() => setActiveId(preview.id)}
                          onFocus={() => setActiveId(preview.id)}
                          onMouseEnter={() => setActiveId(preview.id)}
                          className={[
                            "flex h-16 items-center gap-2 rounded-2xl px-3 text-left text-xs font-semibold transition",
                            isActive
                              ? "bg-[#007AFF] text-white shadow-[0_14px_30px_-22px_rgba(0,122,255,0.7)]"
                              : "bg-[#f6efe3] text-[#625847] hover:bg-[#efe3d0]",
                          ].join(" ")}
                        >
                          <span
                            className={[
                              "grid h-8 w-8 flex-shrink-0 place-items-center rounded-full",
                              isActive ? "bg-white/15 text-[#f4d18d]" : "bg-white text-[#9a7942]",
                            ].join(" ")}
                          >
                            {preview.icon}
                          </span>
                          <span>{preview.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="order-1 flex items-center justify-center lg:order-2">
                <div className="relative w-full max-w-[430px]">
                  <div className="pointer-events-none absolute -inset-12 bg-sky-glow blur-2xl" />
                  <PhoneFrame key={active.id} screenId={active.id} scale={0.78} className="relative transition-transform duration-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-20 md:px-12 md:py-28 lg:px-20 lg:py-32">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-neutral-400">Scripture-grounded AI</p>
          <p className="mx-auto mt-8 max-w-3xl text-center text-3xl leading-relaxed text-neutral-500">
            A daily verse prepared for you, and a companion who answers from the Word:
            book, chapter, and verse. Never a replacement for your pastor. Always in your pocket.
          </p>
        </div>
      </div>
    </section>
  );
}
